import {
  CSSProperties,
  KeyboardEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  Loader2,
  MessageCircle,
  Send,
  SquarePen,
  User,
  Users,
  X,
} from 'lucide-react';
import {
  fetchConversationMessages,
  fetchConversations,
  fetchMessagingContacts,
  markConversationRead,
  MESSAGING_REFRESH_MS,
  MessagingConversation,
  MessagingConversationKind,
  MessagingMember,
  MessagingMessage,
  MessagingThread,
  openDirectConversation,
  openOfficeConversation,
  sendConversationMessage,
} from '../api/candidate';
import '../messaging.css';
import dayjs, {
  fmtDate,
  fmtDayLong,
  fmtDayShort,
  fmtTime,
  isSameDay,
  isToday,
} from '../lib/dates';
import { Photo, useInstructorPhotoByName, useMyPhotos } from '../components/Photos';

const CONVERSATIONS_KEY = ['candidate', 'messaging', 'conversations'];
const threadKey = (id: string) => ['candidate', 'messaging', 'thread', id];
const MAX_BODY_LENGTH = 4000;
/** Marge basse de la vue (pb-6), sous la carte ; s'y ajoute la marge basse réelle de la page (place de la
 *  barre de navigation du téléphone, refonte du 14/10/2026), lue au moment de la mesure. */
const VIEW_BOTTOM = 24;

/** Message du serveur, affiché tel quel ; à défaut, un texte de repli. */
function serverMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const message = (error.response?.data as { message?: unknown } | undefined)
      ?.message;
    if (typeof message === 'string' && message) return message;
    if (Array.isArray(message) && message.length > 0) return message.join(' ');
  }
  return fallback;
}

/** Conversation retirée ou accès fermé : insister n'y changera rien. */
function isGone(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    [403, 404].includes(error.response?.status ?? 0)
  );
}

/** UUID v4 ; `randomUUID` n'existe qu'en contexte sécurisé (HTTPS, localhost). */
function newRequestId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10).join(''),
  ].join('-');
}

/** Réunit des messages sans doublon, du plus ancien au plus récent. */
function mergeMessages(
  a: MessagingMessage[],
  b: MessagingMessage[],
): MessagingMessage[] {
  const byId = new Map<string, MessagingMessage>();
  for (const message of a) byId.set(message.id, message);
  for (const message of b) byId.set(message.id, message);
  return [...byId.values()].sort((x, y) => x.sequence - y.sequence);
}

/**
 * Relecture périodique : la page la plus récente s'ajoute au fil déjà affiché
 * (messages précédents compris). Si elle n'a aucun message en commun avec lui
 * — longue absence —, on repart d'elle plutôt que de laisser un trou.
 */
function withLatestPage(
  shown: MessagingThread | undefined,
  page: MessagingThread,
): MessagingThread {
  if (!shown || !page.hasMore) return page;
  const known = new Set(shown.items.map((message) => message.id));
  if (!page.items.some((message) => known.has(message.id))) return page;
  return {
    ...page,
    items: mergeMessages(shown.items, page.items),
    hasMore: shown.hasMore,
  };
}

function withOlderPage(
  shown: MessagingThread,
  older: MessagingThread,
): MessagingThread {
  return {
    ...shown,
    items: mergeMessages(older.items, shown.items),
    hasMore: older.hasMore,
  };
}

function capitalize(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** « Aujourd'hui », « Hier », sinon le jour en toutes lettres. */
function dayLabel(iso: string): string {
  const day = dayjs(iso);
  if (isToday(iso)) return 'Aujourd’hui';
  if (day.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Hier';
  return capitalize(
    day.isSame(dayjs(), 'year') ? fmtDayShort(iso) : fmtDayLong(iso),
  );
}

/** Heure pour aujourd'hui, date sinon. */
function shortDate(iso: string): string {
  if (isToday(iso)) return fmtTime(iso);
  return dayjs(iso).isSame(dayjs(), 'year')
    ? dayjs(iso).format('D MMM')
    : fmtDate(iso);
}

function threadSubtitle(
  kind: MessagingConversationKind,
  members: MessagingMember[],
): string {
  if (kind === 'OFFICE') return 'Le secrétariat de votre auto-école';
  if (kind === 'DIRECT') return 'Moniteur de votre auto-école';
  return members.length > 0
    ? `Groupe · ${members.map((member) => member.name).join(', ')}`
    : 'Groupe';
}

/** Initiales d'un nom, pour sa pastille : deux lettres au plus. */
function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();
}

/**
 * Pastille d'une conversation, comme dans l'espace de l'auto-école : les
 * initiales du moniteur, un pictogramme pour le secrétariat et les groupes.
 * `title` absent : le pictogramme seul (choix du destinataire).
 */
function KindAvatar({
  kind,
  title,
  online = false,
  photo,
}: {
  kind: MessagingConversationKind;
  title?: string;
  /** Moniteur ou secrétariat en ligne : pastille verte. */
  online?: boolean;
  /** Photo du moniteur (échange à deux), posée sur ses initiales. */
  photo?: string | null;
}) {
  const Icon = kind === 'OFFICE' ? Building2 : kind === 'GROUP' ? Users : User;
  return (
    <span className="messaging-avatar" aria-hidden="true">
      {kind === 'DIRECT' && title ? initials(title) : <Icon size={17} />}
      {kind === 'DIRECT' && <Photo url={photo} />}
      {online && <span className="messaging-presence-dot" />}
    </span>
  );
}

function UnreadPill({ count }: { count: number }) {
  return (
    <span className="messaging-unread relative">
      <span aria-hidden="true">{count > 99 ? '99+' : count}</span>
      <span className="sr-only">{` ${count} non lu${count > 1 ? 's' : ''}`}</span>
    </span>
  );
}

function Loading() {
  return (
    <div className="messaging-hint flex-1 flex items-center justify-center p-6">
      <Loader2 className="animate-spin mr-2" size={16} aria-hidden="true" />
      Chargement…
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
      <p role="alert" className="messaging-alert">
        {message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="messaging-button messaging-button--secondary messaging-button--small"
      >
        Réessayer
      </button>
    </div>
  );
}

/** Une relecture a échoué alors que des données sont déjà affichées. */
function RefreshNotice({ onRetry }: { onRetry: () => void }) {
  return (
    <div role="status" className="messaging-notice">
      <span>Actualisation impossible pour le moment.</span>
      <button type="button" onClick={onRetry}>
        Réessayer
      </button>
    </div>
  );
}

/** Choix du destinataire : le secrétariat ou un moniteur de l'école, jamais un groupe. */
function NewMessageDialog({
  onClose,
  onOpened,
}: {
  onClose: () => void;
  onOpened: (
    id: string,
    title: string,
    kind: MessagingConversationKind,
  ) => void;
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const contacts = useQuery({
    queryKey: ['candidate', 'messaging', 'contacts'],
    queryFn: fetchMessagingContacts,
  });
  const staffPhotos = useMyPhotos().staff;
  const open = useMutation({
    mutationFn: (target: { instructorId: string | null; title: string }) =>
      target.instructorId
        ? openDirectConversation(target.instructorId)
        : openOfficeConversation(),
    onSuccess: ({ id }, target) =>
      onOpened(id, target.title, target.instructorId ? 'DIRECT' : 'OFFICE'),
  });

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    // Le focus reste dans la fenêtre tant qu'elle est ouverte.
    const buttons = panelRef.current?.querySelectorAll<HTMLButtonElement>(
      'button:not([disabled])',
    );
    if (!buttons || buttons.length === 0) return;
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    const current = document.activeElement;
    if (e.shiftKey && (current === first || current === panelRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && current === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const instructors = contacts.data?.instructors ?? [];
  const office = !!contacts.data?.office;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="messaging-dialog"
      >
        <div className="flex items-center justify-between mb-1">
          <h2 id={titleId}>Nouveau message</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="messaging-close"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <p className="mb-3">À qui souhaitez-vous écrire ?</p>

        {contacts.isPending ? (
          <Loading />
        ) : contacts.isError ? (
          <ErrorState
            message={serverMessage(
              contacts.error,
              'Impossible de charger vos contacts.',
            )}
            onRetry={() => void contacts.refetch()}
          />
        ) : !office && instructors.length === 0 ? (
          <p className="messaging-empty">
            Aucun contact disponible pour le moment.
          </p>
        ) : (
          // Les pastilles ne portent ici qu'un pictogramme : la ligne ne dit que le nom.
          <div className="messaging-list max-h-[60vh] overflow-y-auto">
            {office && (
              <div>
                <button
                  type="button"
                  disabled={open.isPending}
                  onClick={() =>
                    open.mutate({ instructorId: null, title: 'Secrétariat' })
                  }
                  className="messaging-option"
                >
                  <KindAvatar kind="OFFICE" />
                  <span>Secrétariat</span>
                </button>
              </div>
            )}
            {instructors.length > 0 && (
              <>
                <h3>Moniteurs</h3>
                <ul>
                  {instructors.map((instructor) => (
                    <li key={instructor.id}>
                      <button
                        type="button"
                        disabled={open.isPending}
                        onClick={() =>
                          open.mutate({
                            instructorId: instructor.id,
                            title: instructor.name,
                          })
                        }
                      >
                        <KindAvatar kind="DIRECT" photo={staffPhotos[instructor.id]} />
                        <span className="truncate">{instructor.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {open.isError && (
          <p role="alert" className="messaging-alert mt-3">
            {serverMessage(open.error, 'Impossible d’ouvrir cet échange.')}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Un message du fil, à plat : auteur, heure, texte — la présentation de
 * l'espace de l'auto-école. L'élève s'y lit sous « Vous », sans son nom.
 */
function MessageLine({
  message,
  dayStart,
  staffTag,
}: {
  message: MessagingMessage;
  /** Libellé du jour quand ce message ouvre une journée. */
  dayStart: string | null;
  staffTag: boolean;
}) {
  const me = useMyPhotos().me;
  const photoOf = useInstructorPhotoByName();
  return (
    <div
      className={`messaging-message${
        message.mine
          ? ' messaging-message--mine'
          : message.authorKind === 'student'
            ? ' messaging-message--student'
            : ''
      }`}
    >
      {dayStart && <div className="messaging-day">{dayStart}</div>}
      <div className="messaging-author-line">
        <span className="messaging-message-avatar" aria-hidden="true">
          {message.mine ? <User size={15} /> : initials(message.author)}
          <Photo
            url={
              message.mine
                ? me
                : message.authorKind === 'staff'
                  ? photoOf(message.author)
                  : null
            }
          />
        </span>
        <strong>
          {message.mine ? 'Vous' : message.author}
          {!message.mine && staffTag && <small> · Auto-école</small>}
          {/* Toujours dit : l'élève sait qu'il lit une réponse automatique. */}
          {message.authorKind === 'assistant' && (
            <small> · Réponse automatique</small>
          )}
        </strong>
        <time dateTime={message.createdAt}>{fmtTime(message.createdAt)}</time>
      </div>
      <p className="messaging-text">{message.body}</p>
    </div>
  );
}

function ThreadPanel({
  conversationId,
  fallback,
  focusComposer,
  onBack,
}: {
  conversationId: string;
  /** Titre et nature connus avant le chargement du fil. */
  fallback: { title: string; kind: MessagingConversationKind };
  /** Vrai après « Nouveau message » : l'élève vient pour écrire. */
  focusComposer: boolean;
  onBack: () => void;
}) {
  const photoOf = useInstructorPhotoByName();
  const queryClient = useQueryClient();
  const key = threadKey(conversationId);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  /** Distance au bas du fil à conserver après l'ajout de messages précédents. */
  const anchor = useRef<number | null>(null);
  const stickToBottom = useRef(true);
  /** Dernière séquence déjà signalée comme lue. */
  const marked = useRef(0);
  /** Envoi en cours : le même texte retenté garde le même identifiant. */
  const attempt = useRef<{ requestId: string; body: string } | null>(null);
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);

  const thread = useQuery({
    queryKey: key,
    queryFn: async () => {
      const page = await fetchConversationMessages(conversationId);
      return withLatestPage(
        queryClient.getQueryData<MessagingThread>(key),
        page,
      );
    },
    retry: (failures, error) => !isGone(error) && failures < 1,
    refetchInterval: (query) =>
      isGone(query.state.error) ? false : MESSAGING_REFRESH_MS,
    // Au retour sur l'application, sans attendre la prochaine relecture.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const data = thread.data;
  const items = data?.items ?? [];
  const firstSequence = items.length > 0 ? items[0].sequence : 0;
  const lastSequence = items.length > 0 ? items[items.length - 1].sequence : 0;
  const kind = data?.conversation.kind ?? fallback.kind;
  const title = data?.conversation.title ?? fallback.title;
  // Conversation retirée ou accès fermé : on n'en montre plus rien.
  const failed = thread.isError && (!data || isGone(thread.error));
  const canWrite = !failed && data?.conversation.canWrite !== false;

  const older = useMutation({
    mutationFn: (before: number) =>
      fetchConversationMessages(conversationId, before),
    onSuccess: (page) => {
      const log = logRef.current;
      if (log) anchor.current = log.scrollHeight - log.scrollTop;
      queryClient.setQueryData<MessagingThread>(
        key,
        (shown) => shown && withOlderPage(shown, page),
      );
    },
  });

  const send = useMutation({
    mutationFn: (payload: { requestId: string; body: string }) =>
      sendConversationMessage(conversationId, payload),
    onSuccess: (sent, payload) => {
      attempt.current = null;
      // Le serveur tient pour lu tout ce qui précède le message envoyé.
      marked.current = Math.max(marked.current, sent.sequence);
      stickToBottom.current = true;
      setDraft('');
      // Affichage immédiat, remplacé par la version du serveur à la relecture.
      queryClient.setQueryData<MessagingThread>(
        key,
        (shown) =>
          shown && {
            ...shown,
            items: mergeMessages(shown.items, [
              {
                id: sent.id,
                sequence: sent.sequence,
                mine: true,
                authorKind: 'student',
                author: '',
                body: payload.body,
                createdAt: new Date().toISOString(),
              },
            ]),
          },
      );
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
      inputRef.current?.focus();
    },
    onError: (error) =>
      setSendError(
        serverMessage(error, 'Votre message n’a pas pu être envoyé.'),
      ),
  });

  const submit = () => {
    const body = draft.trim();
    if (!body || send.isPending) return;
    if (attempt.current?.body !== body)
      attempt.current = { requestId: newRequestId(), body };
    setSendError(null);
    send.mutate(attempt.current);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    submit();
  };

  // À l'ouverture : la saisie après « Nouveau message » ; sur téléphone, où la
  // liste disparaît, le titre du fil.
  useEffect(() => {
    if (focusComposer) inputRef.current?.focus();
    else if (backRef.current?.offsetParent) headingRef.current?.focus();
  }, [focusComposer]);

  // Le fil reste calé en bas, sauf si l'élève est remonté lire ; les messages
  // précédents s'ajoutent sans déplacer ce qu'il lit.
  useLayoutEffect(() => {
    const log = logRef.current;
    if (!log) return;
    if (anchor.current !== null) {
      log.scrollTop = log.scrollHeight - anchor.current;
      anchor.current = null;
    } else if (stickToBottom.current) {
      log.scrollTop = log.scrollHeight;
    }
  }, [firstSequence, lastSequence]);

  // La zone de saisie grandit avec le texte, jusqu'à quelques lignes.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    const borders = input.offsetHeight - input.clientHeight;
    input.style.height = `${Math.min(input.scrollHeight + borders, 136)}px`;
  }, [draft]);

  // Fil ouvert : lu jusqu'au dernier message affiché.
  useEffect(() => {
    if (lastSequence <= marked.current) return;
    const previous = marked.current;
    marked.current = lastSequence;
    markConversationRead(conversationId, lastSequence)
      .then(() => {
        queryClient.setQueryData<MessagingConversation[]>(
          CONVERSATIONS_KEY,
          (list) =>
            list?.map((c) => (c.id === conversationId ? { ...c, unread: 0 } : c)),
        );
        void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
      })
      .catch(() => {
        // Sans gravité : nouvel essai au prochain message reçu.
        if (marked.current === lastSequence) marked.current = previous;
      });
  }, [conversationId, lastSequence, queryClient]);

  return (
    <>
      <header>
        {/* Grand écran : la liste reste à côté, le retour n'a pas lieu d'être. */}
        <button
          ref={backRef}
          type="button"
          onClick={onBack}
          aria-label="Retour aux conversations"
          className="messaging-back messaging-back--narrow"
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <KindAvatar
          kind={kind}
          title={title}
          online={!!data?.conversation.presence?.online}
          photo={photoOf(title)}
        />
        <div>
          <h2 ref={headingRef} tabIndex={-1}>
            {title}
          </h2>
          <p
            title={kind === 'GROUP' ? threadSubtitle(kind, data?.members ?? []) : undefined}
          >
            {threadSubtitle(kind, data?.members ?? [])}
            {data?.conversation.presence?.online && (
              <>
                {' · '}
                <span className="messaging-presence-label">En ligne</span>
              </>
            )}
          </p>
        </div>
      </header>

      {thread.isError && !failed && (
        <RefreshNotice onRetry={() => void thread.refetch()} />
      )}

      {failed ? (
        <ErrorState
          message={serverMessage(
            thread.error,
            'Impossible de charger cette conversation.',
          )}
          onRetry={() => void thread.refetch()}
        />
      ) : !data ? (
        <Loading />
      ) : (
        <div
          ref={logRef}
          role="log"
          aria-label="Fil de discussion"
          tabIndex={0}
          onScroll={(e) => {
            const log = e.currentTarget;
            stickToBottom.current =
              log.scrollHeight - log.scrollTop - log.clientHeight < 80;
          }}
          className="messaging-log"
        >
          {data.hasMore && (
            <div className="messaging-more">
              <button
                type="button"
                disabled={older.isPending}
                onClick={() => older.mutate(firstSequence)}
                className="messaging-button messaging-button--secondary messaging-button--small"
              >
                {older.isPending && (
                  <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                )}
                Messages précédents
              </button>
              {older.isError && (
                <p role="alert" className="messaging-alert">
                  {serverMessage(
                    older.error,
                    'Les messages précédents n’ont pas pu être chargés.',
                  )}
                </p>
              )}
            </div>
          )}
          {items.length === 0 ? (
            <div className="messaging-empty h-full">
              <strong>Aucun message pour le moment.</strong>
              {canWrite && <span>Écrivez le premier message ci-dessous.</span>}
            </div>
          ) : (
            items.map((message, index) => {
              const previous = index > 0 ? items[index - 1] : null;
              const newDay =
                !previous || !isSameDay(previous.createdAt, message.createdAt);
              return (
                <MessageLine
                  key={message.id}
                  message={message}
                  dayStart={newDay ? dayLabel(message.createdAt) : null}
                  staffTag={kind === 'GROUP' && message.authorKind === 'staff'}
                />
              );
            })
          )}
        </div>
      )}

      {!failed && !canWrite && (
        <p className="messaging-readonly">
          Vous ne pouvez pas écrire dans cette conversation.
        </p>
      )}
      {canWrite && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="messaging-composer"
        >
          {sendError && (
            <div role="alert" className="messaging-send-error">
              <span>{sendError}</span>
              <button type="button" onClick={submit}>
                Réessayer
              </button>
            </div>
          )}
          <textarea
            ref={inputRef}
            aria-label="Votre message"
            placeholder="Rédigez un message…"
            rows={1}
            maxLength={MAX_BODY_LENGTH}
            value={draft}
            readOnly={send.isPending}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <div className="messaging-composer-tools">
            <span className="messaging-hint hidden sm:inline">
              Entrée pour envoyer, Maj + Entrée pour aller à la ligne.
            </span>
            {draft.length >= MAX_BODY_LENGTH - 200 && (
              <span className="messaging-hint">
                {draft.length} / {MAX_BODY_LENGTH}
              </span>
            )}
            <button
              type="submit"
              aria-label="Envoyer le message"
              disabled={!draft.trim() || send.isPending}
              className="messaging-send"
            >
              {send.isPending ? (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              ) : (
                <Send size={16} aria-hidden="true" />
              )}
              Envoyer
            </button>
          </div>
        </form>
      )}
    </>
  );
}

interface Selection {
  id: string;
  title: string;
  kind: MessagingConversationKind;
  /** Ouverte depuis « Nouveau message ». */
  compose: boolean;
}

export default function MessagesView() {
  const queryClient = useQueryClient();
  const photoOf = useInstructorPhotoByName();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [choosing, setChoosing] = useState(false);
  // Signalement d'un problème au service technique Permio : une fenêtre, pas une conversation.
  const newButtonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  /** Conversation à retrouver dans la liste au retour du fil (téléphone). */
  const returnTo = useRef<string | null>(null);

  const conversationsQuery = useQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: fetchConversations,
    refetchInterval: MESSAGING_REFRESH_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  const conversations = conversationsQuery.data ?? [];

  // Les cartes descendent jusqu'au bas de l'écran. Leur hauteur est mesurée :
  // bandeaux et onglets au-dessus d'elles varient d'un élève et d'un écran à
  // l'autre.
  const threadOpen = selection !== null;
  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const card = cardRef.current;
      if (!card) return;
      const top = card.getBoundingClientRect().top + window.scrollY;
      const main = card.closest('main');
      const pageBottom = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 32;
      setCardHeight(Math.round(window.innerHeight - top - VIEW_BOTTOM - pageBottom));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [threadOpen]);

  useEffect(() => {
    if (selection || !returnTo.current) return;
    const id = returnTo.current;
    returnTo.current = null;
    Array.from(listRef.current?.querySelectorAll('button') ?? [])
      .find((button) => button.dataset.conversation === id)
      ?.focus();
  }, [selection]);

  return (
    <div className="messaging flex flex-col gap-5 pb-6">
      <div
        className={`flex-col gap-3 sm:flex-row sm:items-end sm:justify-between ${
          selection ? 'hidden lg:flex' : 'flex'
        }`}
      >
        <header className="messaging-page-heading">
          <h1>Messages</h1>
          <p>Écrivez au secrétariat ou aux moniteurs de votre auto-école.</p>
        </header>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            ref={newButtonRef}
            type="button"
            onClick={() => setChoosing(true)}
            className="messaging-button flex-1"
          >
            <SquarePen size={16} aria-hidden="true" />
            Nouveau message
          </button>
        </div>
      </div>

      {/* Téléphone : la liste, à sa hauteur naturelle, puis le fil.
          Grand écran : deux cartes côte à côte, comme dans l'espace de l'auto-école. */}
      <div
        ref={cardRef}
        style={
          cardHeight === null
            ? undefined
            : ({ '--card-height': `${cardHeight}px` } as CSSProperties)
        }
        className={`lg:grid lg:grid-cols-[22rem_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:gap-6 lg:h-[var(--card-height)] lg:min-h-[24rem] ${
          selection ? 'h-[var(--card-height)] min-h-[24rem]' : ''
        }`}
      >
        <section
          aria-label="Conversations"
          className={`messaging-card flex-col min-h-0 overflow-hidden ${
            selection ? 'hidden lg:flex' : 'flex'
          }`}
        >
          <div className="messaging-inbox-heading px-5 pt-5">
            <div>
              <h2>Conversations</h2>
              {conversations.length > 0 && (
                <p>
                  {conversations.filter((c) => c.unread > 0).length} non lue(s)
                </p>
              )}
            </div>
          </div>
          {conversationsQuery.isError && conversationsQuery.data && (
            <RefreshNotice onRetry={() => void conversationsQuery.refetch()} />
          )}
          {conversationsQuery.isPending ? (
            <Loading />
          ) : conversationsQuery.isError && !conversationsQuery.data ? (
            <ErrorState
              message={serverMessage(
                conversationsQuery.error,
                'Impossible de charger vos conversations.',
              )}
              onRetry={() => void conversationsQuery.refetch()}
            />
          ) : conversations.length === 0 ? (
            <div className="messaging-empty flex-1">
              <span className="messaging-symbol" aria-hidden="true">
                <MessageCircle size={26} />
              </span>
              <strong>Aucune conversation pour le moment.</strong>
              <span>
                Utilisez « Nouveau message » pour écrire au secrétariat ou à un
                moniteur.
              </span>
            </div>
          ) : (
            <ul
              ref={listRef}
              className="messaging-list flex-1 min-h-0 px-4 pb-2 lg:overflow-y-auto"
            >
              {conversations.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    data-conversation={c.id}
                    aria-current={selection?.id === c.id ? 'true' : undefined}
                    onClick={() =>
                      setSelection({
                        id: c.id,
                        title: c.title,
                        kind: c.kind,
                        compose: false,
                      })
                    }
                  >
                    {/* Le nom d'abord dans le document ; la pastille est replacée à gauche par la feuille de style. */}
                    <span className="messaging-list-copy">
                      <strong>
                        {c.title}
                        {c.presence?.online && <span className="sr-only"> (en ligne)</span>}
                      </strong>
                      <small>
                        {c.kind === 'GROUP' ? (
                          <span className="messaging-group-mark">Groupe</span>
                        ) : c.kind === 'OFFICE' ? (
                          'Votre auto-école'
                        ) : (
                          'Moniteur'
                        )}
                      </small>
                      <small className="messaging-list-last">
                        {c.last ? c.last.body : 'Aucun message pour le moment'}
                      </small>
                    </span>
                    <KindAvatar
                      kind={c.kind}
                      title={c.title}
                      online={!!c.presence?.online}
                      photo={photoOf(c.title)}
                    />
                    <span className="messaging-list-meta">
                      {c.last && (
                        <time dateTime={c.last.at}>{shortDate(c.last.at)}</time>
                      )}
                      {c.unread > 0 && <UnreadPill count={c.unread} />}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section
          aria-label={selection ? `Conversation : ${selection.title}` : 'Conversation'}
          className={`min-h-0 h-full ${selection ? 'block' : 'hidden lg:block'}`}
        >
          <div className="messaging-card messaging-conversation h-full">
            {selection ? (
              <ThreadPanel
                key={selection.id}
                conversationId={selection.id}
                fallback={{ title: selection.title, kind: selection.kind }}
                focusComposer={selection.compose}
                onBack={() => {
                  returnTo.current = selection.id;
                  setSelection(null);
                }}
              />
            ) : (
              <div className="messaging-empty flex-1">
                <span className="messaging-symbol" aria-hidden="true">
                  <MessageCircle size={26} />
                </span>
                <span>
                  {conversations.length > 0
                    ? 'Choisissez une conversation pour lire vos messages.'
                    : 'Vos échanges avec votre auto-école s’afficheront ici.'}
                </span>
              </div>
            )}
          </div>
        </section>
      </div>

      {choosing && (
        <NewMessageDialog
          onClose={() => {
            setChoosing(false);
            newButtonRef.current?.focus();
          }}
          onOpened={(id, title, kind) => {
            setChoosing(false);
            setSelection({ id, title, kind, compose: true });
            void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
          }}
        />
      )}
    </div>
  );
}
